import * as THREE from 'three';
import { Noise } from './Noise.js';
import { SeededRandom } from '../utils/random.js';
import { FLORA, PLANET } from '../utils/constants.js';

/* ── Per-biome small-foliage styles ──────────────────────────── */

const FOLIAGE_STYLES = {
  forest: {
    types: ['grass_tuft', 'glow_pod', 'fern_small', 'flower'],
    baseColor: [0.28, 0.60, 0.30],
    accentColor: [0.65, 0.70, 0.55],
    glows: true,
    density: 1.0,
  },
  ocean: {
    types: ['seaweed', 'anemone', 'bubble_plant'],
    baseColor: [0.50, 0.55, 0.35],
    accentColor: [0.55, 0.65, 0.50],
    glows: true,
    density: 0.7,
  },
  ice: {
    types: ['ice_flower', 'frost_fern', 'snow_tuft'],
    baseColor: [0.58, 0.20, 0.75],
    accentColor: [0.56, 0.50, 0.85],
    glows: true,
    density: 0.5,
  },
  crystal: {
    types: ['crystal_shard', 'glow_pod', 'prism_flower'],
    baseColor: [0.78, 0.60, 0.55],
    accentColor: [0.82, 0.75, 0.65],
    glows: true,
    density: 0.8,
  },
  lava: {
    types: ['ash_tuft', 'ember_pod', 'heat_vent_moss'],
    baseColor: [0.02, 0.30, 0.15],
    accentColor: [0.04, 0.85, 0.45],
    glows: true,
    density: 0.45,
  },
  desert: {
    types: ['dry_grass', 'sand_flower', 'pebble_moss'],
    baseColor: [0.10, 0.50, 0.45],
    accentColor: [0.14, 0.60, 0.60],
    glows: false,
    density: 0.55,
  },
  rocky: {
    types: ['lichen', 'moss_patch', 'dry_grass'],
    baseColor: [0.12, 0.25, 0.30],
    accentColor: [0.20, 0.35, 0.38],
    glows: false,
    density: 0.5,
  },
};

/**
 * Generates small ground-cover foliage (grass, flowers, lichen, pods, etc.)
 * scattered densely across the planet surface.
 *
 * Uses InstancedMesh for performance — hundreds of small objects rendered
 * with minimal draw-call overhead.
 */
export class FoliageSystem {
  constructor() {
    /** @type {{ id: string, type: string, position: {x,y,z}, direction: {x,y,z}, scale: number }[]} */
    this.foliage = [];
    this.group = new THREE.Group();
    this.group.name = 'foliage';
    this._instancedMeshes = [];
  }

  /* ── public API ────────────────────────────────── */

  generate(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    const style = FOLIAGE_STYLES[planetData.type];
    if (!style || planetData.type === 'gas') {
      return { group: this.group, foliage: this.foliage };
    }

    const rng   = new SeededRandom(planetData.seed + 33333);
    const noise = new Noise(planetData.seed + 44444);

    const areaFactor = Math.min(3, Math.max(0.5, (planetRadius / PLANET.DENSITY_REF_RADIUS) ** 2));
    const baseCount = Math.round(
      (FLORA.MIN_FOLIAGE + rng.next() * (FLORA.MAX_FOLIAGE - FLORA.MIN_FOLIAGE)) * style.density * areaFactor,
    );

    // Pre-generate all foliage data points
    const points = [];
    let attempts = 0;

    while (points.length < baseCount && attempts < baseCount * 3) {
      attempts++;

      const u     = rng.next();
      const v     = rng.next();
      const theta = Math.acos(2 * u - 1);
      const phi   = 2 * Math.PI * v;
      const dir   = new THREE.Vector3(
        Math.sin(theta) * Math.cos(phi),
        Math.cos(theta),
        Math.sin(theta) * Math.sin(phi),
      ).normalize();

      // Density noise — creates patches of foliage
      const nVal = noise.fbm(dir.x * 6, dir.y * 6, dir.z * 6, 3) * 0.5 + 0.5;
      if (nVal < 0.12) continue;

      // Keep site clearings barren — ruins and the landing site should read
      // as deliberate open places. Ground cover elsewhere is fine.
      if (plan && !plan.clearOfTag(dir, 0.5, 'clearing')) continue;

      const surfaceHeight = getHeightFn(dir);
      const foliageSeed = planetData.seed + points.length * 37 + 55;
      const fRng = new SeededRandom(foliageSeed);
      const foliageType = fRng.pick(style.types);
      const scale = FLORA.FOLIAGE_MIN_SCALE + fRng.next() * (FLORA.FOLIAGE_MAX_SCALE - FLORA.FOLIAGE_MIN_SCALE);

      const data = {
        id: `foliage_${planetData.seed}_${points.length}`,
        type: foliageType,
        position: { x: dir.x * surfaceHeight, y: dir.y * surfaceHeight, z: dir.z * surfaceHeight },
        direction: { x: dir.x, y: dir.y, z: dir.z },
        scale,
      };
      points.push(data);
    }

    this.foliage = points;

    // Group by type for instanced rendering
    const byType = {};
    for (const pt of points) {
      if (!byType[pt.type]) byType[pt.type] = [];
      byType[pt.type].push(pt);
    }

    for (const [type, items] of Object.entries(byType)) {
      this._createInstancedBatch(type, items, style);
    }

    return { group: this.group, foliage: this.foliage };
  }

  clear() {
    for (const im of this._instancedMeshes) {
      im.geometry.dispose();
      im.material.dispose();
      this.group.remove(im);
    }
    this._instancedMeshes = [];
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    this.foliage = [];
  }

  /* ── instanced batch builder ───────────────────── */

  _createInstancedBatch(type, items, style) {
    const { geometry, material } = this._getPrototype(type, style);
    const im = new THREE.InstancedMesh(geometry, material, items.length);
    im.castShadow = true;
    im.receiveShadow = true;
    im.name = `foliage-batch-${type}`;

    const dummy = new THREE.Object3D();

    for (let i = 0; i < items.length; i++) {
      const data = items[i];
      const pos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
      const dir = new THREE.Vector3(data.direction.x, data.direction.y, data.direction.z);

      dummy.position.copy(pos);
      const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      dummy.quaternion.copy(quat);
      // Random spin + scale
      const spinRng = new SeededRandom(data.id.length + i * 97);
      dummy.rotateOnAxis(new THREE.Vector3(0, 1, 0), spinRng.next() * Math.PI * 2);
      dummy.scale.setScalar(data.scale);
      dummy.updateMatrix();

      im.setMatrixAt(i, dummy.matrix);
    }

    im.instanceMatrix.needsUpdate = true;
    this.group.add(im);
    this._instancedMeshes.push(im);
  }

  _getPrototype(type, style) {
    const baseMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.baseColor),
      roughness: 0.8,
      metalness: 0.0,
    });

    if (style.glows) {
      baseMat.emissive = new THREE.Color().setHSL(style.accentColor[0], style.accentColor[1], style.accentColor[2] * 0.3);
      baseMat.emissiveIntensity = 0.08;
    }

    const accentMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...style.accentColor),
      roughness: 0.6,
      metalness: 0.05,
    });
    if (style.glows) {
      accentMat.emissive = new THREE.Color().setHSL(...style.accentColor);
      accentMat.emissiveIntensity = 0.2;
    }

    switch (type) {
      case 'grass_tuft':
      case 'dry_grass':
      case 'snow_tuft':
      case 'ash_tuft': {
        const geo = new THREE.ConeGeometry(0.5, 2.2, 4);
        return { geometry: geo, material: baseMat };
      }

      case 'glow_pod':
      case 'ember_pod': {
        const geo = new THREE.SphereGeometry(0.6, 6, 5);
        geo.scale(1, 1.3, 1);
        return { geometry: geo, material: accentMat };
      }

      case 'fern_small':
      case 'frost_fern': {
        const geo = new THREE.ConeGeometry(0.9, 1.8, 4);
        geo.scale(1, 1, 0.5);
        return { geometry: geo, material: baseMat };
      }

      case 'flower':
      case 'sand_flower':
      case 'prism_flower':
      case 'ice_flower': {
        const geo = new THREE.SphereGeometry(0.5, 5, 4);
        geo.scale(1.2, 0.8, 1.2);
        return { geometry: geo, material: accentMat };
      }

      case 'seaweed': {
        const geo = new THREE.CylinderGeometry(0.12, 0.18, 2.5, 4);
        return { geometry: geo, material: baseMat };
      }

      case 'anemone': {
        const geo = new THREE.SphereGeometry(0.8, 6, 6, 0, Math.PI * 2, 0, Math.PI * 0.6);
        return { geometry: geo, material: accentMat };
      }

      case 'bubble_plant': {
        const geo = new THREE.SphereGeometry(0.6, 6, 6);
        const mat = accentMat.clone();
        mat.transparent = true;
        mat.opacity = 0.6;
        return { geometry: geo, material: mat };
      }

      case 'crystal_shard': {
        const geo = new THREE.ConeGeometry(0.3, 1.8, 4);
        const mat = accentMat.clone();
        mat.roughness = 0.1;
        mat.metalness = 0.4;
        return { geometry: geo, material: mat };
      }

      case 'lichen':
      case 'moss_patch':
      case 'pebble_moss':
      case 'heat_vent_moss': {
        const geo = new THREE.SphereGeometry(0.6, 5, 4);
        geo.scale(2.0, 0.4, 2.0);
        return { geometry: geo, material: baseMat };
      }

      default: {
        const geo = new THREE.SphereGeometry(0.5, 5, 4);
        return { geometry: geo, material: baseMat };
      }
    }
  }
}
